import { SeeqNames } from 'generated-seeqnames/app.constants.seeqnames';
import { sqWorkbookStore } from '@/core/core.stores';
import { isScalingTableAssetTreeCreationEnabled } from '@/services/systemConfiguration.utilities';
import { BaseTableDefinitionStore } from '@/tableDefinitionEditor/baseTableDefinitionStore';
import {
  ADD_TO_TREE_PSEUDO_RULE,
  CREATE_TREE_PSEUDO_RULE,
} from '@/tableDefinitionEditor/columnRules/columnRuleBuilder.constants';
import {
  SCALING_TABLE_DEFINITION_CANCELLATION_GROUP,
  SCALING_TABLE_FETCH_CANCELLATION_GROUP,
} from '@/tableDefinitionEditor/tableDefinition.constants';
import { TableTypeEnum } from '@seeqdev/api/model/TableDefinitionInputV1';
import type { ColumnRule } from '@/tableDefinitionEditor/columnRules/columnRule.constants';

const DefaultType = TableTypeEnum.SCALING;

/**
 * Store for the scaling table definition
 * The table definition state is stored here
 * which is updated when we receive messages from the server on the socket for table definitions.
 * The corresponding actions file will be the templated tableDefinition.actions file.
 * The base TableDefinitionStore class contains all handler functions, getters and state management
 */
export class ScalingTableDefinitionStore extends BaseTableDefinitionStore {
  defaultName = 'ITEM_FINDER.DEFAULT_NAME';
  initialize(): void {
    this.setupState(DefaultType);
  }

  get name(): string {
    // While an existing table is being fetched, the stored name is blank but a real name exists on
    // the server. Synthesizing a new-table default here would flash the wrong name in the toolbar.
    if (this.isLoadingDefinition) {
      return this.state.get('name');
    }
    return this.getName({
      currentName: this.state.get('name'),
      tableDefinitions: sqWorkbookStore.tableDefinitions,
      id: this.id,
    });
  }

  getRulesNotAllowed(): ColumnRule[] {
    let rulesNotAllowedInScalingTable: ColumnRule[] = [SeeqNames.MaterializedTables.Rules.SearchAncestor];
    if (this.tableType === TableTypeEnum.SCALINGCSV || !this.hasHierarchySearch) {
      rulesNotAllowedInScalingTable = rulesNotAllowedInScalingTable.concat([
        SeeqNames.MaterializedTables.Rules.EventAssetCreator,
      ]);
    }
    if (!isScalingTableAssetTreeCreationEnabled()) {
      rulesNotAllowedInScalingTable = rulesNotAllowedInScalingTable.concat([
        SeeqNames.MaterializedTables.Rules.AssetCreator,
        SeeqNames.MaterializedTables.Rules.TreePathCreator,
        CREATE_TREE_PSEUDO_RULE,
        ADD_TO_TREE_PSEUDO_RULE,
      ]);
    }
    return rulesNotAllowedInScalingTable;
  }

  getMaterializedTableCancellationGroup(): string {
    return SCALING_TABLE_FETCH_CANCELLATION_GROUP;
  }

  getTableDefinitionCancellationGroup(): string {
    return SCALING_TABLE_DEFINITION_CANCELLATION_GROUP;
  }

  static readonly storeName = 'sqScalingTableDefinitionStore';

  override handlers = {
    SCALING_TABLE_DEFINITION_SET_SUBSCRIBER_ID: this.setSubscriberId,
    SCALING_TABLE_DEFINITION_SET_SHOULD_SEARCH_FOR_ITEMS: this.setShouldSearchForItems,
    SCALING_TABLE_DEFINITION_SET_NAME: this.setName,
    SCALING_TABLE_DEFINITION_SET_SEARCH_TYPE_AI: this.setSearchTypeAI,
    SCALING_TABLE_DEFINITION_SET_DESCRIPTION: this.setDescription,
    SCALING_TABLE_DEFINITION_SET_BATCH_ACTION: this.setBatchAction,
    SCALING_TABLE_DEFINITION_SET_DO_TABLE_RELOAD: this.setDoTableReload,
    SCALING_TABLE_DEFINITION_SET_TABLE_DEFINITION: this.setTableDefinition,
    SCALING_TABLE_DEFINITION_SET_ITEM_FINDER: this.setItemFinder,
    SCALING_TABLE_DEFINITION_SET_MATERIALIZED_TABLE: this.setMaterializedTable,
    SCALING_TABLE_DEFINITION_SET_EVALUATION_RESULTS: this.setEvaluationResults,
    SCALING_TABLE_DEFINITION_RESET: this.reset,
    SCALING_TABLE_DEFINITION_ADD_PENDING_TABLE_ID: this.addPendingTableId,
    SCALING_TABLE_DEFINITION_REMOVE_PENDING_TABLE_ID: this.removePendingTableId,
    SCALING_TABLE_DEFINITION_SET_CREATED_ITEM_FINDER_ID: this.setCreatedItemFinderId,
    SCALING_TABLE_DEFINITION_SET_ALLOW_HIDDEN_COLUMNS_AS_INPUTS: this.setAllowHiddenColumnsAsInputs,
    SCALING_TABLE_DEFINITION_SET_AG_GRID_API: this.setAgGridApi,
    SCALING_TABLE_DEFINITION_SET_ROW_DATA_IS_LOADING: this.setRowDataIsLoading,
    SCALING_TABLE_DEFINITION_SET_IS_LOADING_DEFINITION: this.setIsLoadingDefinition,
    REQUEST_SET_PROGRESS: this.requestSetProgress,
    SCALING_TABLE_DEFINITION_SET_SELECTED_COLUMNS: this.setSelectedColumns,
    SCALING_TABLE_DEFINITION_SET_SHOW_CONFIGURE_COLUMN: this.setShowConfigureColumn,
    SCALING_TABLE_DEFINITION_SET_SCROLL_TO_COLUMN_ID: this.setScrollToColumnId,
    SCALING_TABLE_DEFINITION_SET_SHOW_CREATE_TEMPLATE: this.setShowCreateTemplate,
    SCALING_TABLE_DEFINITION_SET_IS_APPLYING_TEMPLATE: this.setIsApplyingTemplate,
    SCALING_TABLE_DEFINITION_SET_TEMPLATE_INPUT_TO_SELECT: this.setTemplateInputToSelect,
    SCALING_TABLE_DEFINITION_SET_COLUMN_ID_TO_BE_USED_AS_TEMPLATE_INPUT: this.setColumnIdToBeUsedAsTemplateInput,
    SCALING_TABLE_DEFINITION_SET_TEMPLATE_COLUMNS: this.setTemplateColumns,
    SCALING_TABLE_DEFINITION_SET_IS_EDITING_TEMPLATE: this.setIsEditingTemplate,
    SCALING_TABLE_DEFINITION_SET_EDIT_TEMPLATE_PREVIEW_COLUMNS: this.setEditTemplatePreviewColumns,
    SCALING_TABLE_DEFINITION_SET_EDIT_TEMPLATE_PREVIEW_TABLE_COLUMNS: this.setEditTemplatePreviewTableColumns,
    SCALING_TABLE_DEFINITION_SET_EDIT_TEMPLATE_REMOVED_OUTPUT_BY_COLUMN_ID: this.setEditTemplateRemovedOutputByColumnId,
    SCALING_TABLE_DEFINITION_SET_COLUMNS: this.setColumns,
    SCALING_TABLE_DEFINITION_SET_COLUMN_TO_DELETE: this.setColumnToDelete,
    SCALING_TABLE_DEFINITION_SET_SHOW_RETIRE_TEMPLATE_MODAL: this.setShowRetireTemplateModal,
    SCALING_TABLE_DEFINITION_SET_STORED_TABLE_FAILURES: this.setStoredTableFailures,
    SCALING_TABLE_DEFINITION_SET_STORED_TABLES: this.setStoredTables,
    SCALING_TABLE_DEFINITION_SET_INPUT_FEATURE_COLUMN_IDS: this.setInputFeatureColumnIds,
  };
}
